import React, { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import QRCode from "qrcode";
import { Button } from "../ui/Button";
import { Label } from "../ui/Label";
import { OTPInput } from "../ui/OTPInput";
import { ErrorMessage } from "../ui/ErrorMessage";
import { AuthService } from "../../services/auth.service.real";
import { ProfileService } from "../../services/profile.service";

export const MFASetup: React.FC = () => {
  const [step, setStep] = useState<"setup" | "verify" | "complete">("setup");
  const [qrCode, setQrCode] = useState("");
  const [qrCodeUrl, setQrCodeUrl] = useState(""); // Store otpauth URI
  const [secret, setSecret] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [showPhoneVerificationError, setShowPhoneVerificationError] = useState(true);

  // Get current MFA status and phone verification from User
  const currentUser = AuthService.getCurrentUser();
  const userProfile = ProfileService.getUserProfileFromStorage();
  const [isMFAEnabled, setIsMFAEnabled] = useState(currentUser?.mfaRequired || false);
  const phoneVerified = userProfile?.phoneVerified || false;

  // Generate QR code image from otpauth URI
  useEffect(() => {
    if (qrCodeUrl) {
      QRCode.toDataURL(qrCodeUrl, { width: 256, margin: 1 })
        .then((dataUrl) => {
          setQrCode(dataUrl);
        })
        .catch((err) => {
          console.error("Failed to generate QR code:", err);
        });
    }
  }, [qrCodeUrl]);

  const initiateMutation = useMutation({
    mutationFn: () => AuthService.initiateMFA(),
    onSuccess: (data) => {
      setQrCodeUrl(data.qrCode || ""); // Store otpauth URI
      setSecret(data.secret || "");
      setStep("verify");
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (code: string) => AuthService.verifyMFASetup(code),
    onSuccess: () => {
      setStep("complete");
      setIsMFAEnabled(true);

      // Update mfaRequired in current user
      const currentUser = AuthService.getCurrentUser();
      if (currentUser) {
        const updatedUser = { ...currentUser, mfaRequired: true };
        AuthService.saveCurrentUser(updatedUser);
      }

      // Trigger profile API call after 500ms delay
      setTimeout(async () => {
        try {
          const user = AuthService.getCurrentUser();
          if (user?.id) {
            const updatedUserFromProfile = await ProfileService.getUserProfile(user.id);
            AuthService.saveCurrentUser(updatedUserFromProfile);
            ProfileService.saveUserProfile(updatedUserFromProfile);
          }
        } catch (error) {
          console.warn("Failed to refresh user profile after MFA verification:", error);
        }
      }, 500);
    },
  });

  const handleInitiate = async () => {
    await initiateMutation.mutateAsync();
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6) return;
    await verifyMutation.mutateAsync(verificationCode);
  };

  return (
    <div className="bg-white border border-border-gray rounded-none p-6 max-w-80 sm:max-w-full md:w-4/6">
      <div className="mb-6">
        <h3 className="text-base font-bold uppercase tracking-wide">MFA</h3>
        <p className="text-sm text-gray-600 mt-2">Add an Extra Layer of Security to Your Account</p>
      </div>

      {/* Current Status */}
      <div className="mb-6 p-4 bg-white border border-border-gray rounded-none">
        <div>
          <p className="text-sm font-bold text-black">
            {isMFAEnabled ? "MFA is Enabled" : "MFA is Not Enabled"}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            {isMFAEnabled
              ? "Your Account is Protected with Multi-Factor Authentication"
              : "Enable Multi-Factor Authentication for Better Security"}
          </p>
        </div>
      </div>

      {/* Phone Verification Requirement */}
      {!phoneVerified && showPhoneVerificationError && (
        <ErrorMessage
          message="Phone Verification required. You must verify your Phone Number before enabling MFA. Please go to the Phone Number tab to complete Phone Verification first."
          onDismiss={() => setShowPhoneVerificationError(false)}
          className="mb-6"
        />
      )}

      {step === "setup" && !isMFAEnabled && phoneVerified && (
        <div className="space-y-4">
          <Button
            onClick={handleInitiate}
            disabled={initiateMutation.isPending}
            className="px-8 h-12 bg-primary-blue hover:bg-primary-blue/90 text-white rounded-none text-sm"
          >
            {initiateMutation.isPending ? "Setting up..." : "Set Up MFA"}
          </Button>
        </div>
      )}

      {step === "verify" && (
        <div className="space-y-5 max-w-sm sm:max-w-2xl">
          <div className="bg-white border border-border-gray rounded-none p-6 text-center">
            {qrCode ? (
              <>
                <p className="text-sm text-gray-700 mb-4">
                  Scan this QR-code with Your Authenticator App:
                </p>
                <div className="bg-white inline-block">
                  <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-600">Loading QR Code...</p>
            )}

            {secret && (
              <div className="mt-6">
                <p className="text-sm text-gray-700 mb-3">Or Enter this Key Manually:</p>
                <div className="flex items-center justify-center gap-3">
                  <div className="h-10 sm:h-12 bg-disabled-bg border border-border-gray rounded-none px-4 py-3 text-sm max-w-40 sm:max-w-full font-mono overflow-y-hidden overflow-x-scroll">
                    {secret}
                  </div>
                  <Button
                    variant="outline"
                    className="px-6 h-10 bg-primary-blue hover:bg-primary-blue/90 text-white rounded-none text-sm border-0"
                    onClick={() => {
                      navigator.clipboard.writeText(secret);
                    }}
                  >
                    Copy
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-black">
              Enter 6-digit Code from Your App
            </Label>
            <OTPInput
              value={verificationCode}
              onChange={setVerificationCode}
              error={verifyMutation.isError}
            />
            {verifyMutation.isError && (
              <p className="text-xs text-red-500">Invalid Code. Please Try Again.</p>
            )}
          </div>

          <div className="flex space-x-3">
            <Button
              onClick={handleVerify}
              disabled={verificationCode.length !== 6 || verifyMutation.isPending}
              className="px-8 h-12 bg-primary-blue hover:bg-primary-blue/90 text-white rounded-none text-sm"
            >
              {verifyMutation.isPending ? "Verifying..." : "Verify and Enable"}
            </Button>
          </div>
        </div>
      )}

      {step === "complete" && (
        <div className="space-y-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-none">
            <p className="text-sm font-bold text-amber-900 mb-2">Important:</p>
            <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
              <li>Keep Your Authenticator App Installed and Backed Up</li>
              <li>Save Your Recovery Codes in a Safe Place</li>
              <li>You'll Need the 6-digit Code Every Time You Log In</li>
            </ul>
          </div>
        </div>
      )}

      {/*{isMFAEnabled && step === "setup" && (*/}
      {/*  <div className="space-y-4">*/}
      {/*    <Button*/}
      {/*      variant="outline"*/}
      {/*      className="text-red-600 border-red-600 hover:bg-red-50"*/}
      {/*      onClick={() => {*/}
      {/*        // Disable MFA functionality*/}
      {/*        setIsMFAEnabled(false);*/}

      {/*        // Update mfaRequired in current user*/}
      {/*        const currentUser = AuthService.getCurrentUser();*/}
      {/*        if (currentUser) {*/}
      {/*          const updatedUser = { ...currentUser, mfaRequired: false };*/}
      {/*          AuthService.saveCurrentUser(updatedUser);*/}
      {/*        }*/}
      {/*      }}*/}
      {/*    >*/}
      {/*      Reset MFA*/}
      {/*    </Button>*/}
      {/*  </div>*/}
      {/*)}*/}
    </div>
  );
};
